import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A cart upsell card rendered above the cart totals.
 *
 * Adding this product to the cart is an "upgrade": the current cart contents are cleared
 * first and replaced with only this product, then the cart drawer updates in place (no
 * page refresh) via the same `CartLinesUpdateEvent` the rest of the theme listens for.
 *
 * @typedef {object} CartUpsellRefs
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} [addToCartButtonText]
 * @property {HTMLElement} [errorMessage]
 * @extends {Component<CartUpsellRefs>}
 */
export class CartUpsellComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @param {MouseEvent} event */
  handleClick(event) {
    event.preventDefault();

    if (this.hasAttribute('loading')) return;

    this.#upgradeCart();
  }

  async #upgradeCart() {
    const { addToCartButton, addToCartButtonText, errorMessage } = this.refs;
    const variantId = this.dataset.variantId;

    if (!variantId) return;

    this.setAttribute('loading', '');
    addToCartButton.disabled = true;
    errorMessage?.classList.add('hidden');

    if (addToCartButtonText) {
      addToCartButtonText.textContent = addToCartButton.dataset.loadingLabel || addToCartButtonText.textContent;
    }

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .filter((item) => item instanceof HTMLElement && item.dataset.sectionId)
      .map((item) => /** @type {HTMLElement} */ (item).dataset.sectionId);

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const clearResponse = await fetch(Theme.routes.cart_clear_url, fetchConfig('json'));
      if (!clearResponse.ok) throw new Error(`Failed to clear cart: ${clearResponse.status}`);

      const addResponse = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
          }),
        })
      );

      const addResponseJson = await addResponse.json();

      if (!addResponse.ok || addResponseJson.status) {
        throw new Error(addResponseJson.message || addResponseJson.description || 'Failed to add product to cart');
      }

      const cart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json());

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-upsell-component',
          sourceId: this.id || 'cart-upsell',
          itemCount: cart.item_count,
          sections: addResponseJson.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferredEventPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      if (errorMessage) {
        errorMessage.textContent = error?.message || Theme.translations.cart_upsell_error || 'Something went wrong. Please try again.';
        errorMessage.classList.remove('hidden');
      }

      if (addToCartButtonText) {
        addToCartButtonText.textContent = addToCartButton.dataset.label || addToCartButtonText.textContent;
      }

      addToCartButton.disabled = false;
    } finally {
      this.removeAttribute('loading');
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
